import React, { useState } from "react";
import { Trash2 } from "lucide-react";
import { TspCard, TspTable } from "../../../components/primitives";
import { DISTRIBUTION_METHODS, DISTRIBUTION_METHOD_LABEL } from "../../../constants/tspOperations";

export const CUSTOM_BOOK_VALUE = "__custom__";
export const LANGUAGE_OPTIONS = ["Hindi", "English", "Marathi", "Gujarati", "Tamil", "Telugu", "Kannada", "Bengali", "Other"];

const EMPTY_DRAFT = { bookChoice: "", customBookName: "", language: "", qty: 1, method: "" };

// Local staging: any number of entries added here are held in the browser until
// "Submit Book Distribution" (below the table) sends the whole batch in one
// request (see TspAddBookDistributionPage.jsx) — nothing is saved a row at a time.
export default function BookDistributionRecordsCard({ records, setRecords, bookOptions, disabled }) {
  const [draft, setDraft] = useState(EMPTY_DRAFT);
  const [error, setError] = useState("");
  const total = records.reduce((sum, row) => sum + Number(row.qty || 0), 0);

  const handleAddEntry = () => {
    const qty = Number(draft.qty);
    const bookLabel = draft.bookChoice === CUSTOM_BOOK_VALUE ? draft.customBookName.trim() : bookOptions.find((b) => String(b.id) === draft.bookChoice)?.book_name;
    if (!bookLabel || !draft.language || !draft.method || !Number.isInteger(qty) || qty < 1) {
      setError("Choose a book, language and method, and enter a quantity of at least 1.");
      return;
    }
    setError("");
    setRecords([
      ...records,
      {
        id: `r-${Date.now()}-${Math.random()}`,
        bookType: bookLabel,
        book_set_id: draft.bookChoice === CUSTOM_BOOK_VALUE ? null : Number(draft.bookChoice),
        custom_book_name: draft.bookChoice === CUSTOM_BOOK_VALUE ? bookLabel : null,
        language: draft.language,
        qty,
        method: draft.method,
      },
    ]);
    setDraft(EMPTY_DRAFT);
  };

  const handleDelete = (id) => setRecords(records.filter((row) => row.id !== id));

  const columns = [
    { key: "bookType", label: "Book Type" },
    { key: "language", label: "Language", render: (row) => <span className="tsp-records-muted">{row.language}</span> },
    { key: "qty", label: "Quantity", render: (row) => <span className="tsp-records-qty">{row.qty}</span> },
    { key: "method", label: "Distribution Method", render: (row) => <span className="tsp-records-muted">{DISTRIBUTION_METHOD_LABEL[row.method]}</span> },
    {
      key: "delete",
      label: "Delete",
      align: "right",
      render: (row) => (
        <button type="button" className="tsp-row-delete-btn" onClick={() => handleDelete(row.id)} disabled={disabled} aria-label={`Remove ${row.bookType}`}>
          <Trash2 size={15} />
        </button>
      ),
    },
  ];

  return (
    <TspCard title="Book Distribution Records" className="tsp-records-card">
      <TspTable className="tsp-table-records" columns={columns} rows={records} rowKey="id" footer={{ bookType: "Total", qty: `${total} Books` }} />

      <div className="tsp-add-entry-row">
        <span className="tsp-add-entry-label">Add Distribution Entry</span>
        <div className="tsp-add-entry-fields">
          <select className="tsp-field-control" value={draft.bookChoice} aria-label="Book Type" disabled={disabled} onChange={(e) => setDraft({ ...draft, bookChoice: e.target.value })}>
            <option value="">Book Type</option>
            {bookOptions.map((book) => (
              <option key={book.id} value={book.id}>
                {book.book_name}
              </option>
            ))}
            <option value={CUSTOM_BOOK_VALUE}>Other / Not Listed</option>
          </select>
          {draft.bookChoice === CUSTOM_BOOK_VALUE && (
            <input
              type="text"
              className="tsp-field-control"
              placeholder="Book name"
              value={draft.customBookName}
              disabled={disabled}
              onChange={(e) => setDraft({ ...draft, customBookName: e.target.value })}
            />
          )}
          <select className="tsp-field-control" value={draft.language} aria-label="Language" disabled={disabled} onChange={(e) => setDraft({ ...draft, language: e.target.value })}>
            <option value="">Language</option>
            {LANGUAGE_OPTIONS.map((lang) => (
              <option key={lang} value={lang}>
                {lang}
              </option>
            ))}
          </select>
          <input
            type="number"
            min="1"
            step="1"
            className="tsp-field-control tsp-bd-qty"
            value={draft.qty}
            disabled={disabled}
            onChange={(e) => setDraft({ ...draft, qty: e.target.value })}
            aria-label="Quantity"
            placeholder="1"
          />
          <select className="tsp-field-control" value={draft.method} aria-label="Distribution Method" disabled={disabled} onChange={(e) => setDraft({ ...draft, method: e.target.value })}>
            <option value="">Distribution Method</option>
            {DISTRIBUTION_METHODS.map((method) => (
              <option key={method.value} value={method.value}>
                {method.label}
              </option>
            ))}
          </select>
          <button type="button" className="tsp-btn tsp-btn-red tsp-btn-sm tsp-bd-add-entry" onClick={handleAddEntry} disabled={disabled}>
            Add Entry
          </button>
        </div>
        {error && (
          <p className="tsp-form-error" role="alert">
            {error}
          </p>
        )}
      </div>
    </TspCard>
  );
}
